import { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useSalesControlling } from '../hooks/useSalesControlling';
import { SurchargeAllocationTab } from '../components/sales-controlling/SurchargeAllocationTab';
import { PriceOverviewTab } from '../components/sales-controlling/PriceOverviewTab';
import { SalesOverviewTab } from '../components/sales-controlling/SalesOverviewTab';
import type { SCTab } from '../types/salesControlling';

const TABS: { key: SCTab; label: string }[] = [
    { key: 'price-overview', label: 'Price Overview' },
    { key: 'surcharge-allocation', label: 'Surcharge Allocation' },
    // { key: 'sales-overview', label: 'Sales Overview' }, // temporarily hidden
];

/** Format a local Date as yyyy-MM-dd without UTC conversion */
function fmtDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Calculate period dates based on preset and year.
 * "Last complete month" = the month before today's month.
 * E.g. on 2026-04-01: last complete month = March 2026.
 */
function getPeriodDates(preset: string, year: number): { from: string; to: string } {
    const now = new Date();
    // Last complete month: for current year = month before current, for past years = December
    const lastCompleteMonth = year < now.getFullYear() ? 12 : now.getMonth(); // 1-based (getMonth() returns 0-based, but for "last complete" it's correct as 1-based)

    switch (preset) {
        case 'YTD': {
            // Jan 1 to end of last complete month
            const end = new Date(year, lastCompleteMonth, 0); // day 0 = last day of prev month
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
        case 'MTD': {
            // First to last day of last complete month
            const start = new Date(year, lastCompleteMonth - 1, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        case '3M': {
            // First day 3 months back to last day of last complete month
            const start = new Date(year, lastCompleteMonth - 3, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        default: {
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
    }
}

/** Detect which period preset matches the current dates */
function detectPeriod(dateFrom: string, dateTo: string, year: number): string {
    for (const preset of ['YTD', 'MTD', '3M']) {
        const dates = getPeriodDates(preset, year);
        if (dates.from === dateFrom && dates.to === dateTo) return preset;
    }
    return '';
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export function SalesControllingPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/sales-controlling');
    const { filterOptions, filters, updateFilters, loading, error } = useSalesControlling();
    const [activeTab, setActiveTab] = useState<SCTab>('price-overview');
    const [refreshKey, setRefreshKey] = useState(0);
    const [surchargeDocumentFilter, setSurchargeDocumentFilter] = useState('');
    const savedYear = (() => { try { const v = sessionStorage.getItem('sc-year'); return v ? parseInt(v, 10) : currentYear; } catch { return currentYear; } })();
    const [selectedYear, setSelectedYear] = useState(savedYear);
    const [activePeriod, setActivePeriod] = useState<string>(() => detectPeriod(filters.dateFrom, filters.dateTo, savedYear));

    const handleYearChange = useCallback((year: number) => {
        setSelectedYear(year);
        try { sessionStorage.setItem('sc-year', String(year)); } catch { /* ignore */ }
        if (activePeriod) {
            const dates = getPeriodDates(activePeriod, year);
            updateFilters({ dateFrom: dates.from, dateTo: dates.to });
        }
    }, [activePeriod, updateFilters]);

    const handlePeriodClick = useCallback((preset: string) => {
        setActivePeriod(preset);
        const dates = getPeriodDates(preset, selectedYear);
        updateFilters({ dateFrom: dates.from, dateTo: dates.to });
    }, [selectedYear, updateFilters]);

    const handleDateChange = useCallback((field: 'dateFrom' | 'dateTo', value: string) => {
        const newFrom = field === 'dateFrom' ? value : filters.dateFrom;
        const newTo = field === 'dateTo' ? value : filters.dateTo;
        setActivePeriod(detectPeriod(newFrom, newTo, selectedYear));
        updateFilters({ [field]: value });
    }, [updateFilters, filters.dateFrom, filters.dateTo, selectedYear]);

    if (permLoading || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900">Sales Controlling</h1>
                    {canEdit && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            Edit Access
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setRefreshKey(k => k + 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap items-end gap-4">
                    {/* Company */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Company</label>
                        <select
                            value={filters.company}
                            onChange={e => updateFilters({ company: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All Companies</option>
                            {filterOptions.companies.map(c => (
                                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                            ))}
                        </select>
                    </div>

                    {/* Year */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Year</label>
                        <select
                            value={selectedYear}
                            onChange={e => handleYearChange(parseInt(e.target.value, 10))}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            {YEARS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Period Presets */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Period</label>
                        <div className="flex border rounded-md overflow-hidden">
                            {['YTD', 'MTD', '3M'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => handlePeriodClick(p)}
                                    className={`px-3 py-1.5 text-sm ${
                                        activePeriod === p
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {p === '3M' ? '3 Months' : p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date From */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">From</label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={e => handleDateChange('dateFrom', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>

                    {/* Date To */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">To</label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={e => handleDateChange('dateTo', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'surcharge-allocation' && (
                        <SurchargeAllocationTab
                            filters={filters}
                            canEdit={canEdit}
                            refreshKey={refreshKey}
                            documentFilter={surchargeDocumentFilter}
                            onClearDocumentFilter={() => setSurchargeDocumentFilter('')}
                        />
                    )}
                    {activeTab === 'price-overview' && (
                        <PriceOverviewTab
                            filters={filters}
                            filterOptions={filterOptions}
                            refreshKey={refreshKey}
                            onNavigateToSurcharge={(documentCode) => {
                                setSurchargeDocumentFilter(documentCode);
                                setActiveTab('surcharge-allocation');
                            }}
                        />
                    )}
                    {activeTab === 'sales-overview' && (
                        <SalesOverviewTab
                            filters={filters}
                            filterOptions={filterOptions}
                            refreshKey={refreshKey}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
